import React from 'react';
import { Link } from 'react-router-dom';
import IntegrationTab from '../components/IntegrationTab';
import { useAuth, hasModuleAccess, hasSectionAccess } from '../utils/hooks/useAuth';

export default function CrmIntegration() {
  const { access, loading: authLoading } = useAuth();

  // Check module and section permissions
  const hasCrmAccess = access ? hasModuleAccess('iurm', access) : false;
  const canViewIntegration = access ? hasSectionAccess('iurm', 'Integration', 'view', access) : false;

  // Check other CRM section permissions for tabs
  const canViewOverview = access ? hasSectionAccess('iurm', 'Overview', 'view', access) : false;
  const canViewLeads = access ? hasSectionAccess('iurm', 'Leads', 'view', access) : false;
  const canViewContacts = access ? hasSectionAccess('iurm', 'Contacts', 'view', access) : false;
  const canViewAccounts = access ? hasSectionAccess('iurm', 'Accounts', 'view', access) : false;
  const canViewTasks = access ? hasSectionAccess('iurm', 'Tasks', 'view', access) : false;
  const canViewCampaigns = access ? hasSectionAccess('iurm', 'Campaigns', 'view', access) : false;

  // Show loading while checking permissions
  if (authLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
        Loading permissions...
      </div>
    );
  }

  // Show access denied if no module access or no integration view access
  if (!hasCrmAccess || !canViewIntegration) {
    return (
      <div style={{ 
        padding: '60px 40px', 
        textAlign: 'center',
        backgroundColor: '#fff',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        margin: '20px'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
        <h2 style={{ color: '#111827', marginBottom: '12px' }}>Access Denied</h2>
        <p style={{ color: '#6B7280', fontSize: '16px', marginBottom: '8px' }}>
          You don't have permission to access the Integration section.
        </p>
        <p style={{ color: '#9CA3AF', fontSize: '14px' }}>
          Please contact your administrator to request access.
        </p>
      </div>
    );
  }

  return (
    <div className="crm-integration-container" style={{ padding: '16px', backgroundColor: '#f8f9fa', minHeight: '100%', fontFamily: 'Inter, sans-serif', position: 'relative' }}>
      
      {/* Sub-Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #E5E7EB', marginBottom: '20px', paddingBottom: '0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          {canViewOverview && (
            <Link to="/crm/overview" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="21" x2="9" y2="9"></line></svg>
                Overview
              </div>
            </Link>
          )}
          {canViewLeads && (
            <Link to="/crm/leads" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                Leads
              </div>
            </Link>
          )}
          {canViewContacts && (
            <Link to="/crm/contacts" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                Contacts
              </div>
            </Link>
          )}
          {canViewAccounts && (
            <Link to="/crm/accounts" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
                Accounts
              </div>
            </Link>
          )}
          {canViewTasks && (
            <Link to="/crm/tasks" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>
                Tasks
              </div>
            </Link>
          )}
          {canViewCampaigns && (
            <Link to="/crm/campaigns" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
                Campaigns
              </div>
            </Link>
          )}
          <div className="nav-item active" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '999px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginBottom: '8px', backgroundColor: '#8B5CF6', color: '#fff' }}>
            <div className="nav-dot" style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'currentColor' }}></div>
            MCP Integration
          </div>
        </div>
      </div>

      <IntegrationTab />
    </div>
  );
}